import React, { useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// Simple and reliable PDF download component
export const SimpleCollateralPDF = ({ 
    fileName = 'collateral-document',
    buttonText = 'Download PDF',
    loadingText = 'Generating...',
    onSuccess = () => {},
    onError = (error) => {
        console.error('PDF generation failed:', error);
        alert('Error generating PDF. Please try again.');
    },
    metadata = {},
    ...buttonProps 
}) => {
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

    const generatePDF = async () => {
        try {
            setIsGeneratingPDF(true);
            console.log('🚀 Starting PDF generation...');
            
            // Get the main page container
            const pageElement = document.querySelector('.min-h-screen');
            
            if (!pageElement) {
                console.error('❌ Page element not found');
                throw new Error('Page element not found');
            }

            console.log('✅ Found page element:', pageElement);
            console.log('📏 Element dimensions:', {
                width: pageElement.offsetWidth,
                height: pageElement.offsetHeight,
                scrollHeight: pageElement.scrollHeight,
                clientHeight: pageElement.clientHeight
            });

            // Scroll to top to ensure everything is visible
            window.scrollTo(0, 0);
            console.log('📜 Scrolled to top');

            // Wait a moment for any animations to settle
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Check for images and wait for them to load
            const images = pageElement.querySelectorAll('img');
            console.log(`🖼️  Found ${images.length} images`);

            if (images.length > 0) {
                console.log('⏳ Waiting for images to load...');
                const imagePromises = Array.from(images).map((img, index) => {
                    return new Promise((resolve) => {
                        if (img.complete && img.naturalHeight !== 0) {
                            console.log(`✅ Image ${index + 1} already loaded`);
                            resolve();
                        } else {
                            console.log(`⏳ Waiting for image ${index + 1}: ${img.src.substring(0, 50)}...`);
                            const timeout = setTimeout(() => {
                                console.log(`⚠️  Image ${index + 1} timeout`);
                                resolve();
                            }, 3000);
                            
                            img.onload = () => {
                                console.log(`✅ Image ${index + 1} loaded`);
                                clearTimeout(timeout);
                                resolve();
                            };
                            img.onerror = () => {
                                console.log(`❌ Image ${index + 1} failed to load`);
                                clearTimeout(timeout);
                                resolve();
                            };
                        }
                    });
                });

                await Promise.all(imagePromises);
                console.log('✅ All images processed');
            }

            // Additional wait for rendering
            await new Promise(resolve => setTimeout(resolve, 500));

            // Convert blob URLs to data URLs before html2canvas
            console.log('🔄 Converting blob URLs to data URLs...');
            const imagesInPage = pageElement.querySelectorAll('img');
            const blobToDataUrlMap = new Map();
            
            for (let i = 0; i < imagesInPage.length; i++) {
                const img = imagesInPage[i];
                const src = img.src;
                
                if (src && src.startsWith('blob:')) {
                    console.log(`🔄 Converting blob URL ${i + 1}:`, src);
                    try {
                        // Fetch the blob and convert to data URL
                        const response = await fetch(src);
                        const blob = await response.blob();
                        
                        // Convert blob to data URL
                        const dataUrl = await new Promise((resolve) => {
                            const reader = new FileReader();
                            reader.onloadend = () => resolve(reader.result);
                            reader.readAsDataURL(blob);
                        });
                        
                        blobToDataUrlMap.set(src, dataUrl);
                        console.log(`✅ Converted blob URL ${i + 1} to data URL`);
                    } catch (error) {
                        console.error(`❌ Failed to convert blob URL ${i + 1}:`, error);
                    }
                }
            }
            
            console.log(`✅ Converted ${blobToDataUrlMap.size} blob URLs to data URLs`);

            console.log('🎨 Generating canvas...');

            // Create canvas with html2canvas
            const canvas = await html2canvas(pageElement, {
                scale: 1.5,
                proxy:"http://localhost:4000",
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                logging: true,
                removeContainer: false,
                foreignObjectRendering: true,
                imageTimeout: 10000,
                scrollX: 0,
                scrollY: 0,
                windowWidth: window.innerWidth,
                windowHeight: window.innerHeight,
                onclone: (clonedDoc, element) => {
                    console.log('🔄 Processing cloned document...');
                    
                    // Hide PDF download buttons in the cloned document
                    const buttons = clonedDoc.querySelectorAll('button');
                    buttons.forEach(button => {
                        const text = button.textContent || button.innerText || '';
                        if (text.toLowerCase().includes('download') || text.toLowerCase().includes('pdf')) {
                            button.style.display = 'none';
                            console.log('🚫 Hidden PDF button in clone');
                        }
                    });

                    // Replace blob URLs with data URLs for images
                    const images = clonedDoc.querySelectorAll('img');
                    console.log(`🖼️ Found ${images.length} images in cloned document`);
                    
                    images.forEach((img, index) => {
                        const src = img.src;
                        
                        if (src && src.startsWith('blob:') && blobToDataUrlMap.has(src)) {
                            const dataUrl = blobToDataUrlMap.get(src);
                            img.src = dataUrl;
                            console.log(`✅ Replaced blob URL with data URL for image ${index + 1}`);
                        }
                    });

                    // Ensure body has proper styling
                    const body = clonedDoc.body;
                    if (body) {
                        body.style.margin = '0';
                        body.style.padding = '0';
                        body.style.backgroundColor = '#ffffff';
                    }

                    // Fix any sticky/fixed positioning
                    const allElements = clonedDoc.querySelectorAll('*');
                    allElements.forEach(el => {
                        if (el.style.position === 'fixed' || el.style.position === 'sticky') {
                            el.style.position = 'static';
                        }
                    });

                    console.log('✅ Cloned document processed');
                }
            });

            console.log('✅ Canvas generated:', {
                width: canvas.width,
                height: canvas.height,
                area: canvas.width * canvas.height
            });

            // Validate canvas
            if (canvas.width === 0 || canvas.height === 0) {
                console.error('❌ Canvas has zero dimensions');
                throw new Error('Canvas has zero dimensions');
            }

            // Convert to image data
            const imgData = canvas.toDataURL('image/png', 0.95);
            console.log('🖼️  Image data generated, length:', imgData.length);

            if (imgData === 'data:,' || imgData.length < 1000) {
                console.error('❌ Canvas appears to be empty');
                throw new Error('Canvas appears to be empty');
            }

            // Create PDF
            console.log('📄 Creating PDF...');
            
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });

            // A4 dimensions
            const a4Width = 210; // mm
            const a4Height = 297; // mm
            
            // Calculate height based on canvas aspect ratio
            // Force full width, scale height proportionally
            const pdfWidth = a4Width;
            const pdfHeight = (canvas.height / canvas.width) * a4Width;
            
            console.log('📐 PDF dimensions:', {
                canvasWidth: canvas.width,
                canvasHeight: canvas.height,
                pdfWidth: pdfWidth.toFixed(2),
                pdfHeight: pdfHeight.toFixed(2),
                aspectRatio: (canvas.width / canvas.height).toFixed(2)
            });

            // If content is taller than A4, we need multiple pages
            if (pdfHeight > a4Height) {
                console.log('📄 Content spans multiple pages');
                let position = 0;
                let remainingHeight = pdfHeight;
                
                while (remainingHeight > 0) {
                    // Add image section to current page
                    pdf.addImage(
                        imgData, 
                        'PNG', 
                        0, 
                        -position, 
                        pdfWidth, 
                        pdfHeight
                    );
                    
                    remainingHeight -= a4Height;
                    position += a4Height;
                    
                    // Add new page if there's more content
                    if (remainingHeight > 0) {
                        pdf.addPage();
                    }
                }
            } else {
                // Content fits on single page - add with no offset (full width)
                pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
            }

            // Generate filename
            const prospectName = metadata.prospect_company_name || 'prospect';
            const companyName = metadata.my_company_name || 'company';
            const sanitizedFileName = `${companyName}-${prospectName}-collateral`.replace(/[^a-zA-Z0-9]/g, '-');
            
            console.log('💾 Saving PDF as:', sanitizedFileName);
            
            // Save the PDF
            pdf.save(`${sanitizedFileName}.pdf`);
            
            console.log('✅ PDF generation completed successfully!');
            onSuccess();
            
        } catch (error) {
            console.error('❌ PDF generation failed:', error);
            onError(error);
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    return (
        <button
            onClick={generatePDF}
            disabled={isGeneratingPDF}
            style={{
                position: 'fixed',
                top: '20px',
                right: '20px',
                background: isGeneratingPDF ? '#6b7280' : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 20px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: isGeneratingPDF ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.2s ease',
                opacity: isGeneratingPDF ? 0.7 : 1,
                zIndex: 1000
            }}
            {...buttonProps}
        >
            {isGeneratingPDF ? (
                <>
                    <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid #ffffff',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }}></div>
                    {loadingText}
                </>
            ) : (
                <>
                    <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {buttonText}
                </>
            )}
        </button>
    );
};

// Add the CSS for spinner animation
if (typeof document !== 'undefined') {
    const style = document.createElement('style');
    style.textContent = `
        @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }
    `;
    document.head.appendChild(style);
}

export default SimpleCollateralPDF;
